// ⌘K search over the transaction: columns, events (field / row events,
// validations, joins, cross updates, save steps, follow-ups) and buttons.
// Matches names, headings and what an item DOES — rule text, SQL, button
// parameters. Picking a result selects it in the Design workspace with the
// right inspector tab open.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, ChevronRight } from 'lucide-react'
import type { TransactionModel, TreeNode } from './types'
import { searchIndex, type SearchKind, type SearchResult } from './setupCatalog'

interface Props {
  model: TransactionModel
  open: boolean
  onClose: () => void
  onPick: (node: TreeNode) => void
  /** Phone: full screen instead of a centred panel. */
  fullScreen?: boolean
}

const KINDS: Array<'All' | SearchKind> = ['All', 'Columns', 'Events', 'Buttons']
const GLYPH: Record<SearchKind, { g: string; style: React.CSSProperties }> = {
  Columns: { g: 'T', style: { color: 'var(--color-text)', background: 'var(--color-surface-2)' } },
  Events: { g: 'ƒ', style: { color: 'var(--color-accent)', background: 'var(--color-accent-weak)' } },
  Buttons: { g: '▢', style: { color: '#6929c4', background: 'rgba(105,41,196,.10)' } },
}

export default function SearchPalette({ model, open, onClose, onPick, fullScreen }: Props) {
  const [q, setQ] = useState('')
  const [kind, setKind] = useState<'All' | SearchKind>('All')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const index = useMemo(() => (open ? searchIndex(model) : []), [model, open])

  useEffect(() => {
    if (open) { setCursor(0); setTimeout(() => inputRef.current?.focus(), 0) }
  }, [open])

  const matches = useMemo(() => {
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (!terms.length) return index
    return index.filter(r => terms.every(t => r.name.toLowerCase().includes(t) || r.haystack.includes(t) || r.where.toLowerCase().includes(t)))
  }, [index, q])
  const shown = kind === 'All' ? matches : matches.filter(r => r.kind === kind)
  const groups = (['Columns', 'Events', 'Buttons'] as SearchKind[])
    .filter(k => kind === 'All' || kind === k)
    .map(k => ({ k, items: shown.filter(r => r.kind === k).slice(0, 40) }))
    .filter(g => g.items.length)
  const flat = groups.flatMap(g => g.items)

  if (!open) return null

  const choose = (r: SearchResult) => { onPick(r.node); onClose() }

  return (
    <div role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }} style={{
      position: 'fixed', inset: 0, zIndex: 1200, background: fullScreen ? 'var(--color-bg-secondary)' : 'rgba(0,0,0,0.32)',
      display: 'flex', justifyContent: 'center', alignItems: fullScreen ? 'stretch' : 'flex-start', paddingTop: fullScreen ? 0 : '10vh',
    }}>
      <div role="dialog" aria-label="Search columns, events and buttons" style={{
        width: fullScreen ? '100%' : 'min(640px, 94vw)', maxHeight: fullScreen ? '100%' : '72vh',
        background: 'var(--color-bg-secondary)', borderRadius: fullScreen ? 0 : 10,
        border: fullScreen ? 'none' : '1px solid var(--color-border)', boxShadow: fullScreen ? 'none' : '0 16px 48px rgba(0,0,0,0.24)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>
          <label style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8, border: '2px solid var(--color-accent)', borderRadius: 8,
            padding: '0 10px', height: 44, background: 'var(--color-input-bg)',
          }}>
            <Search size={16} />
            <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Search columns, events and buttons</span>
            <input ref={inputRef} value={q} placeholder="Search columns, events, buttons"
              onChange={e => { setQ(e.target.value); setCursor(0) }}
              onKeyDown={e => {
                if (e.key === 'Escape') { e.preventDefault(); onClose() }
                else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(flat.length - 1, c + 1)) }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(0, c - 1)) }
                else if (e.key === 'Enter' && flat[cursor]) { e.preventDefault(); choose(flat[cursor]) }
              }}
              style={{ font: 'inherit', fontSize: '1.0625rem', border: 'none', outline: 'none', background: 'transparent', flex: 1, minWidth: 0, boxShadow: 'none', padding: 0 }} />
          </label>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} style={{ minHeight: 40 }}>Cancel</button>
        </div>
        <div role="group" aria-label="Filter results" style={{ display: 'flex', gap: 8, padding: '10px 12px', overflowX: 'auto', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
          {KINDS.map(k => {
            const on = kind === k
            const n = k === 'All' ? matches.length : matches.filter(r => r.kind === k).length
            return (
              <button key={k} type="button" aria-pressed={on} onClick={() => { setKind(k); setCursor(0) }} style={{
                font: 'inherit', fontSize: '0.9375rem', whiteSpace: 'nowrap', borderRadius: 16, padding: '5px 12px', display: 'flex', gap: 6, cursor: 'pointer',
                background: on ? 'var(--color-text)' : 'transparent', color: on ? 'var(--color-bg-secondary)' : 'var(--color-text)',
                border: `1px solid ${on ? 'var(--color-text)' : 'var(--color-border)'}`,
              }}>{k} <span style={{ opacity: 0.7 }}>{n}</span></button>
            )
          })}
        </div>
        <div style={{ flex: 1, overflow: 'auto', paddingBottom: 8 }}>
          {flat.length === 0 && (
            <div style={{ padding: 20, color: 'var(--color-text-secondary)' }}>Nothing matches “{q}”.</div>
          )}
          {groups.map(gp => (
            <div key={gp.k}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', padding: '12px 16px 4px', letterSpacing: '0.02em' }}>{gp.k.toUpperCase()}</div>
              {gp.items.map(r => {
                const i = flat.indexOf(r)
                const on = i === cursor
                return (
                  <button key={r.id} type="button" onClick={() => choose(r)} onMouseEnter={() => setCursor(i)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', minHeight: 52, width: '100%', textAlign: 'left',
                    border: 'none', cursor: 'pointer', font: 'inherit', color: 'var(--color-text)',
                    background: on ? 'var(--color-accent-weak)' : 'transparent',
                  }}>
                    <span aria-hidden style={{
                      width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.9375rem', fontWeight: 600, flex: 'none', ...GLYPH[r.kind].style,
                    }}>{GLYPH[r.kind].g}</span>
                    <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: '1rem', fontFamily: r.mono ? 'var(--font-mono)' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                      <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.where}</span>
                    </span>
                    <ChevronRight size={14} style={{ color: 'var(--color-text-faint)' }} />
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
